import React, { useEffect, useState } from 'react'
import { lumi } from '../../lib/lumi'

interface AdminRouteProps {
  children: React.ReactNode
}

export const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const [user, setUser] = useState(lumi.auth.user)
  const [loading, setLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [hasAdminAccess, setHasAdminAccess] = useState(false)

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await lumi.auth.refreshUser()
        const currentUser = lumi.auth.user
        setUser(currentUser)

        if (currentUser) {
          try {
            setIsProcessing(true)
            
            // 1. Verificar se usuário existe no banco de dados
            const { list: existingUsers } = await lumi.entities.users.list({
              filter: { email: currentUser.email }
            })
            
            let dbUser = existingUsers.length > 0 ? existingUsers[0] : null
            
            // 2. Se não existe, criar como USER (ou ADMIN_MASTER se for o primeiro usuário do sistema)
            if (!dbUser) {
              console.log('Sincronizando novo usuário no banco:', currentUser.email)
              
              // Verificar se existem usuários no sistema (bootstrap)
              const { list: allUsers } = await lumi.entities.users.list({})
              const isFirstUser = allUsers.length === 0
              
              dbUser = await lumi.entities.users.create({
                email: currentUser.email,
                name: currentUser.userName || currentUser.email.split('@')[0],
                role: isFirstUser ? 'ADMIN_MASTER' : 'USER', // Primeiro usuário = ADMIN_MASTER
                active: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              })
              
              if (isFirstUser) {
                console.log('✅ Primeiro usuário criado como ADMIN_MASTER (Bootstrap)')
              } else {
                console.log('✅ Novo usuário criado como USER')
                // Redirecionar usuários comuns para área pessoal
                window.location.href = '/minha-conta'
                return
              }
            }
            
            // 3. Verificar se usuário está ativo
            if (!dbUser.active) {
              console.log('❌ Usuário desativado:', currentUser.email)
              setHasAdminAccess(false)
              setLoading(false)
              setIsProcessing(false)
              return
            }
            
            // 4. Definir permissão de acesso baseada no role do banco
            const hasAccess = dbUser.role === 'ADMIN' || dbUser.role === 'ADMIN_MASTER'
            setHasAdminAccess(hasAccess)
            
            if (hasAccess) {
              console.log(`✅ Acesso admin concedido: ${dbUser.role}`)
            } else {
              console.log(`❌ Usuário comum: role = ${dbUser.role} - redirecionando para área pessoal`)
              // Redirecionar usuários comuns para área pessoal
              window.location.href = '/minha-conta'
              return
            }

          } catch (error) {
            console.error('Erro ao processar usuário:', error)
            setHasAdminAccess(false)
          } finally {
            setIsProcessing(false)
          }
        } else {
          setUser(null)
          setHasAdminAccess(false)
        }
      } catch (error) {
        console.error('Falha na verificação de autenticação:', error)
        setUser(null)
        setHasAdminAccess(false)
      } finally {
        setLoading(false)
      }
    }

    checkAuth()

    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      setUser(user)
      if (!user) setHasAdminAccess(false)
    })

    return unsubscribe
  }, [])

  if (loading || isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a] mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isProcessing ? 'Verificando permissões...' : 'Carregando...'}
          </p>
        </div>
      </div>
    )
  }

  // Não autenticado
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Acesso Restrito</h2>
          <p className="text-gray-600 mb-6">Você precisa estar autenticado para acessar o painel administrativo.</p>
          <button
            onClick={() => lumi.auth.signIn()}
            className="bg-[#1e3a8a] text-white px-6 py-3 rounded-lg hover:bg-[#2d4ba5] transition-colors"
          >
            Fazer Login
          </button>
        </div>
      </div>
    )
  }

  // Autenticado mas sem permissão de admin
  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Acesso Negado</h2>
          <p className="text-gray-600 mb-6">Você não possui permissões de administrador.</p>
          <div className="mb-4 text-sm text-gray-500 bg-gray-100 p-3 rounded">
            <p><strong>Usuário:</strong> {user.email}</p>
            <p><strong>Permissão:</strong> Usuário Comum</p>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            Para solicitar acesso administrativo, entre em contato com um Administrador Master.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-[#1e3a8a] text-white px-6 py-3 rounded-lg hover:bg-[#2d4ba5] transition-colors"
          >
            Voltar para Home
          </button>
        </div>
      </div>
    )
  }

  // Admin ou Admin Master autenticado
  return <>{children}</>
}
